import { PageLoader } from '@zeiss/lib-components';
import { useTranslation } from 'react-i18next';
import { withAuthenticationRequired } from 'react-oidc-context';

import { GlobalAction, GlobalResource } from 'src/constants';
import { useGlobalEvaluations } from 'src/hooks';

interface ProtectedRouteProps {
  component: () => JSX.Element;
  requires: {
    actions: GlobalAction[];
    resource: GlobalResource;
  };
}

/**
 * ProtectedRoute
 *
 * - Ensures authentication and authorization before rendering the route
 *   component.
 * - Uses the `withAuthenticationRequired` HOC to check for authentication.
 * - `DecisionCheckedComponent` is passed to the HOC to check for
 *   authorization.
 */
export const ProtectedRoute = ({
  component,
  requires,
}: ProtectedRouteProps) => {
  const { t } = useTranslation(['common']);

  const DecisionCheckedComponent = () => {
    const { can, canUseApp, isError, isLoading } = useGlobalEvaluations();

    if (isLoading) {
      return (
        <PageLoader label={t('common:loadingFeedback.loading')} size="xl" />
      );
    }

    /**
     * 404
     *
     * - Prevents exposing the existence of the app to unauthorized users, or if
     *   the evaluations could not be loaded.
     * - `!canUseApp` must be checked here (not delegated to `!hasAllRequires`)
     *   to ensure we throw 404 rather than 403, hiding that the app exists.
     * - The `RootBoundary` will catch the error and render the desired screen.
     */
    if (isError || !canUseApp) {
      throw new Response('Not Found', { status: 404 });
    }

    const hasAllRequires = requires.actions.every((action) =>
      can({ action, resource: requires.resource }),
    );

    /**
     * 403
     *
     * The `RootBoundary` will catch the error and render the desired screen.
     */
    if (!hasAllRequires) {
      throw new Response('Forbidden', { status: 403 });
    }

    // User is authenticated and authorized, render the component
    const Component = component;
    return <Component />;
  };

  /**
   * Wrap authorization with the authentication HOC to ensure auth redirects
   * happen before.
   */
  const AuthenticatedComponent = withAuthenticationRequired(
    DecisionCheckedComponent,
    {
      OnRedirecting: () => (
        <PageLoader label={t('common:loadingFeedback.loading')} size="xl" />
      ),
    },
  );

  return <AuthenticatedComponent />;
};
